import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { Flex, Hide, Link } from 'pcln-design-system'
import { themeGet } from '@styled-system/theme-get'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import {
  fireEventForViewMoreDeals,
  renderXSellProducts
} from '@/components/CrossSell/inventoryHelper'
import fireRudderstackEvent from '@/shared-utils/rudder-stack'
import { Listing, RudderProductDesc } from './types'
import { HeadingNonXL, HeadingXL } from './InventoryHeading'

const ViewMoreDeals = styled(Link)`
  font-size: 12px;
  font-weight: bold;
`

const ScrollContainer = styled(Flex)`
  overflow: auto hidden;
  & > :last-child {
    margin-right: ${themeGet('space.1')}px;
  }
`

export default function Inventory({
  xSellMessaging,
  xSellProduct,
  rudderChildProductCode,
  rudderParentProductCode,
  xSellUrl,
  listings,
  offerId,
  offerToken
}: {
  xSellMessaging: string
  xSellProduct: 'rental car' | 'flight' | 'hotel'
  rudderChildProductCode: RudderProductDesc
  rudderParentProductCode: RudderProductDesc
  xSellUrl: string
  listings: Listing[]
  offerId: string
  offerToken: string
}) {
  const {
    isMobile = false,
    webstats,
    customer
  } = useContext(BootstrapDataContext)

  useEffect(() => {
    fireRudderstackEvent({
      label: `View more ${xSellProduct} deals`,
      parentProduct: rudderParentProductCode,
      childProduct: rudderChildProductCode,
      source: 'homepage',
      subType: 'cross sell',
      hasInventory: false,
      cguid: webstats.clientGUID,
      emailAddress: customer.emailAddress,
      parentOfferId: offerId,
      rudderEvent: true,
      category: 'impression'
    })
  }, [
    xSellProduct,
    rudderParentProductCode,
    rudderChildProductCode,
    webstats.clientGUID,
    customer.emailAddress,
    offerId
  ])

  const viewMoreDeals = ({ target }: { target?: string } = {}) => (
    <ViewMoreDeals
      data-cguid={webstats.clientGUID}
      data-email-address={customer.emailAddress}
      data-parent-offer-id={offerId}
      data-label={`View more ${xSellProduct} deals`}
      data-child-product={rudderChildProductCode}
      data-parent-product={rudderParentProductCode}
      data-has-inventory={listings.length > 1}
      data-source="homepage"
      data-sub-type="cross sell"
      data-rudder-event
      data-view-more
      onClick={fireEventForViewMoreDeals}
      href={xSellUrl}
      target={target}
      color="primary"
      variation="fill"
      py={2}
      px={3}
    >
      View more {xSellProduct} deals
    </ViewMoreDeals>
  )
  const renderedProducts = renderXSellProducts(
    listings,
    offerId,
    offerToken,
    rudderParentProductCode,
    isMobile ? 2 : 3
  )

  return (
    <>
      <Hide width={8 / 11} mt={40} ml={24} xs sm md lg>
        <HeadingXL xSellMessaging={xSellMessaging}>
          {viewMoreDeals({ target: '_blank' })}
        </HeadingXL>
        <ScrollContainer data-testid="inventory-list" mt={12}>
          {renderedProducts}
        </ScrollContainer>
      </Hide>
      <Hide
        width={[1, null, 2 / 3]}
        mt={[24, null, 3, 40]}
        ml={[0, null, null, 24]}
        mb={[0, null, null, 24]}
        xl
        xxl
      >
        <HeadingNonXL xSellMessaging={xSellMessaging}>
          {viewMoreDeals({ target: '_blank' })}
        </HeadingNonXL>
        <ScrollContainer
          mx={[-3, null, null, 0]}
          px={[3, null, null, 0]}
          mt={24}
        >
          {renderedProducts}
        </ScrollContainer>
      </Hide>
    </>
  )
}
